import { useMemo } from 'react';

import { getSelectorsByUserAgent } from 'react-device-detect';

import { useSearchParameters } from '@/components/signup/hooks/use-search-parameters';
import { JIRA_AI_NATIVE_SIGNUP_SOURCE } from '@/components/user-context/helpers/cohorts';
import { experiments as experimentManifest } from '@/utils/experiment/manifest';
import { getExperimentWithExposures } from '@/utils/experiment/use-experiment';
import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@/utils/feature-flags/feature-flag-manifest';
import { JourneyLocalStorage, useAnchorLocalStorage } from '@/utils/local-storage-hook';

import { useConfirmationPage } from './use-confirmation-page';

export const userRole = {
	n2n: 'userRole.n2n',
};

export const queryParam = {
	templateId: 'queryParam.templateId',
	templateChoice: 'queryParam.templateChoice',
	products: 'queryParam.products',
	teamType: 'queryParam.team_type',
	jtbd: 'queryParam.jtbd',
	jtbdD0Onboarding: 'queryParam.jtbdD0Onboarding',
	jtbdD0OnboardingTemplateTitle: 'queryParam.jtbdD0OnboardingTemplateTitle',
	aiLedOnboarding: 'queryParam.aiLedOnboarding',
	jiraFflCgsKeyExists: 'queryParam.jiraFflCgsKeyExists',
	codingAgents: 'queryParam.coding_agents',
	signupSource: 'queryParam.signupSource',
};

export const localStorageData = {
	team: 'localStorage.team',
	persona: 'localStorage.persona',
};

export const experiment = {
	test: {
		cohort: `experiment.test.cohort`,
		enrolled: `experiment.test.enrolled`,
		value: `experiment.test.value`,
	},
	twcg947TwcLandAiLedOnboarding: {
		cohort: `experiment.twcg947TwcLandAiLedOnboarding.cohort`,
	},
};

export const user = {
	isExistingJiraUser: 'user.isExistingJiraUser',
	isExistingJsmUser: 'user.isExistingJsmUser',
	isN2n: 'user.isN2n',
	isMobile: 'user.isMobile',
};

export type Metadata = string | string[] | boolean | boolean[] | number[] | undefined;

export type RegisteredMetadata = Map<string, Metadata> | undefined;

/**
 * Custom hook to return a key value pair of query params and experiment enrollment data.
 * @returns @type {RegisteredMetadata}
 */
export const useRegisterMetadata = (): RegisteredMetadata => {
	const {
		templateId,
		templateChoice: jiraTemplateChoiceOption,
		teamType,
		jtbd,
		products = [],
		jtbdD0Onboarding,
		jtbdD0OnboardingTemplateTitle,
		aiLedOnboarding,
		jiraFflCgsKey,
		codingAgents,
		signupSource,
	} = useSearchParameters();
	const { experiments = [], availableProducts } = useConfirmationPage();
	const productList = products.map((product) => product);

	// Gather anchor local storage data
	let localStorageTeam: string | undefined;
	let localStoragePersona: string | undefined;

	const { getAnchorLocalStorage } = useAnchorLocalStorage();
	const anchorLocalStorage = getAnchorLocalStorage();
	if (anchorLocalStorage) {
		try {
			const localStorageObj: JourneyLocalStorage = JSON.parse(anchorLocalStorage);
			localStorageTeam = localStorageObj.team;
			localStoragePersona = localStorageObj.persona;
		} catch (e) {
			console.log(e);
		}
	}

	const userAgent = navigator.userAgent;
	const { isMobile } = getSelectorsByUserAgent(userAgent) ?? {
		isMobile: false,
	};

	// eslint-disable-next-line complexity
	const metadata = useMemo(() => {
		const map = new Map<string, string | boolean | string[] | boolean[] | number[]>();
		if (templateId) {
			map.set(queryParam.templateId, templateId);
		}

		if (jiraTemplateChoiceOption) {
			map.set(queryParam.templateChoice, jiraTemplateChoiceOption);
		}

		if (productList) {
			map.set(queryParam.products, productList);
		}

		if (teamType) {
			map.set(queryParam.teamType, teamType);
		}

		if (jtbd) {
			map.set(queryParam.jtbd, jtbd);
		}

		if (jtbdD0Onboarding) {
			map.set(queryParam.jtbdD0Onboarding, jtbdD0Onboarding);
		}

		if (jiraFflCgsKey) {
			map.set(queryParam.jiraFflCgsKeyExists, !!jiraFflCgsKey);
		}

		if (codingAgents) {
			map.set(queryParam.codingAgents, codingAgents);
		}

		if (
			checkFeatureFlagGate(featureFlags.spiceAiSdlcAgenticTemplateForNewSites) &&
			signupSource === JIRA_AI_NATIVE_SIGNUP_SOURCE
		) {
			map.set(queryParam.signupSource, signupSource);
		}

		if (jtbdD0OnboardingTemplateTitle) {
			map.set(queryParam.jtbdD0OnboardingTemplateTitle, jtbdD0OnboardingTemplateTitle);
		}

		if (aiLedOnboarding) {
			map.set(queryParam.aiLedOnboarding, aiLedOnboarding);
		}

		if (localStorageTeam) {
			map.set(localStorageData.team, localStorageTeam);
		}

		if (localStoragePersona) {
			map.set(localStorageData.persona, localStoragePersona);
		}

		if (availableProducts?.sites) {
			if (availableProducts?.sites.length === 0) {
				map.set(user.isN2n, true);
			} else {
				map.set(user.isN2n, false);
			}

			const jiraSites =
				availableProducts?.sites?.filter((site) =>
					site.products.includes('jira-software.ondemand'),
				) ?? [];

			if (jiraSites.length > 0) {
				map.set(user.isExistingJiraUser, true);
			} else {
				map.set(user.isExistingJiraUser, false);
			}

			const jsmSites =
				availableProducts?.sites?.filter((site) =>
					site.products.includes('jira-servicedesk.ondemand'),
				) ?? [];

			if (jsmSites.length > 0) {
				map.set(user.isExistingJsmUser, true);
			} else {
				map.set(user.isExistingJsmUser, false);
			}
		} else {
			map.set(user.isExistingJiraUser, false);
			map.set(user.isExistingJsmUser, false);
			map.set(user.isN2n, true);
		}

		for (const [key, value] of experiments) {
			if (value.enrollmentPreconditionsMet) {
				map.set(`experiment.${key}.cohort`, value.cohort);
			}

			map.set(`experiment.${key}.enrolled`, value.enrollmentPreconditionsMet);
		}

		const twcg947ExperimentStatus = getExperimentWithExposures(
			experimentManifest.twcg947TwcLandAiLedOnboarding,
		);

		if (twcg947ExperimentStatus.type === 'enrolled' && !twcg947ExperimentStatus.usingDefault) {
			map.set(experiment.twcg947TwcLandAiLedOnboarding.cohort, twcg947ExperimentStatus.value);
		} else {
			map.set(experiment.twcg947TwcLandAiLedOnboarding.cohort, 'not-enrolled');
		}

		map.set(user.isMobile, isMobile);

		return map;
	}, [
		templateId,
		productList,
		experiments,
		teamType,
		localStorageTeam,
		localStoragePersona,
		jtbd,
		jtbdD0Onboarding,
		jtbdD0OnboardingTemplateTitle,
		aiLedOnboarding,
		jiraFflCgsKey,
		codingAgents,
	]);

	return metadata;
};
